import * as React from "react"

import { getUrlFromHref } from "../../utils/externalLinks"

const ARROW_ICON_SRC = "/figma/shared/button-arrow-desktop.svg"
const DOWNLOAD_ICON_SRC = "/figma/resources/download-icon.svg"

export type ButtonVariant = "pill" | "download" | "prjkt-ruby" | "text-link"
export type ButtonIcon = "none" | "horizontal" | "down" | "up" | "external" | "download"
export type ButtonStateOverride = "default" | "hover" | "active"

export type ButtonProps = {
  centerLabel?: boolean
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  href?: string
  icon?: ButtonIcon
  iconOnly?: boolean
  leavingSiteInterstitial?: "skip"
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  prjktWrapDesktop?: boolean
  rel?: string
  secondaryLabel?: React.ReactNode
  stateOverride?: ButtonStateOverride
  style?: React.CSSProperties
  target?: React.HTMLAttributeAnchorTarget
  type?: "button" | "submit" | "reset"
  variant?: ButtonVariant
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ")
}

function getPressedClasses(variant: ButtonVariant, stateOverride: ButtonStateOverride) {
  const activeState = stateOverride === "active"

  if (variant === "text-link") {
    return ""
  }

  if (variant === "prjkt-ruby") {
    return joinClasses(
      "active:translate-x-[2px] active:translate-y-[5px] active:shadow-none",
      activeState && "translate-x-[2px] translate-y-[5px] shadow-none",
    )
  }

  return joinClasses(
    "active:translate-x-[1px] active:translate-y-[4px] active:shadow-none",
    "lg:active:translate-x-[2px] lg:active:translate-y-[5px]",
    activeState && "translate-x-[1px] translate-y-[4px] shadow-none lg:translate-x-[2px] lg:translate-y-[5px]",
  )
}

function ButtonGraphic({
  icon,
  variant,
}: {
  icon: Exclude<ButtonIcon, "none">
  variant: ButtonVariant
}) {
  if (icon === "download") {
    return (
      <span
        aria-hidden="true"
        className="flex h-[19px] w-[18px] shrink-0 items-center justify-center lg:h-5 lg:w-5"
      >
        <img
          alt=""
          aria-hidden="true"
          className="h-5 w-5 -scale-y-100"
          src={DOWNLOAD_ICON_SRC}
        />
      </span>
    )
  }

  const wrapperClasses =
    icon === "external"
      ? "flex h-[26px] w-[26px] shrink-0 items-center justify-center lg:h-[31px] lg:w-[31px]"
      : "flex h-[19px] w-[19px] shrink-0 items-center justify-center lg:h-[22px] lg:w-[22px]"

  const transformClasses =
    icon === "down"
      ? "rotate-90"
      : icon === "up"
        ? "-rotate-90"
        : icon === "external"
          ? "-rotate-[31deg] lg:-rotate-45"
          : ""

  const sizeClasses =
    variant === "pill" && icon !== "external"
      ? "h-4 w-4 lg:h-[22px] lg:w-[22px]"
      : "h-[19px] w-[19px] lg:h-[22px] lg:w-[22px]"

  return (
    <span aria-hidden="true" className={wrapperClasses}>
      <img
        alt=""
        aria-hidden="true"
        className={joinClasses(sizeClasses, transformClasses)}
        src={ARROW_ICON_SRC}
      />
    </span>
  )
}

function renderPillContent(
  children: React.ReactNode,
  icon: ButtonIcon,
  centerLabel: boolean,
) {
  if (icon === "none") {
    return <span>{children}</span>
  }

  if (centerLabel) {
    return (
      <>
        <span className="text-center">{children}</span>
        <span className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2">
          <ButtonGraphic icon={icon} variant="pill" />
        </span>
      </>
    )
  }

  return (
    <>
      <span>{children}</span>
      <ButtonGraphic icon={icon} variant="pill" />
    </>
  )
}

function renderDownloadContent(children: React.ReactNode, iconOnly: boolean) {
  return (
    <>
      {iconOnly ? <span className="sr-only">{children}</span> : <span>{children}</span>}
      <ButtonGraphic icon="download" variant="download" />
    </>
  )
}

function renderPrjktContent(
  children: React.ReactNode,
  secondaryLabel: React.ReactNode,
  prjktWrapDesktop: boolean,
) {
  return (
    <div className="flex w-full items-center justify-between gap-3 lg:grid lg:grid-cols-[250px_86px_32px] lg:items-center lg:gap-0">
      <span
        className={joinClasses(
          "max-w-[167px] text-center font-sans font-bold text-button-mobile lg:min-w-0 lg:max-w-none lg:text-center lg:font-utility lg:text-button lg:leading-[1.4]",
          !prjktWrapDesktop && "lg:whitespace-nowrap",
        )}
      >
        {children}
      </span>
      <span className="shrink-0 text-center font-utility text-[14px] font-bold leading-[1.4] text-ink-950 lg:translate-x-3 lg:text-[16px]">
        {secondaryLabel}
      </span>
      <span className="flex items-center justify-center">
        <ButtonGraphic icon="external" variant="prjkt-ruby" />
      </span>
    </div>
  )
}

export const Button = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(
  (
    {
      centerLabel = false,
      children,
      className,
      disabled = false,
      href,
      icon = "none",
      iconOnly = false,
      leavingSiteInterstitial,
      onClick,
      prjktWrapDesktop = false,
      rel,
      secondaryLabel,
      stateOverride = "default",
      style,
      target,
      type = "button",
      variant = "pill",
    },
    ref,
  ) => {
    const [isPressed, setIsPressed] = React.useState(false)
    const pressedClasses = getPressedClasses(variant, stateOverride)
    const resolvedHref = href ? getUrlFromHref(href) : null
    const resolvedTarget = target ?? (resolvedHref ? "_blank" : undefined)
    const resolvedRel =
      rel ?? (resolvedHref ? "noreferrer noopener" : undefined)
    const handlePressStart = () => {
      if (!disabled) {
        setIsPressed(true)
      }
    }
    const handlePressEnd = () => setIsPressed(false)

    const classes =
      variant === "pill"
        ? joinClasses(
            "inline-flex select-none items-center rounded-pill border border-ink-950 font-sans font-bold text-ink-950 transition-[background-color,box-shadow,transform] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-60",
            "h-10 bg-white text-button-mobile shadow-[var(--shadow-button-mobile)] hover:bg-accent-500 active:bg-accent-500",
            "lg:h-[50px] lg:text-button lg:shadow-[var(--shadow-button-desktop)]",
            icon === "none"
              ? "justify-center px-8 lg:px-10"
              : centerLabel
                ? "relative justify-center px-6"
                : "justify-between gap-4 px-6",
            stateOverride === "hover" && "bg-accent-500",
            stateOverride === "active" && "bg-accent-500",
            pressedClasses,
            className,
          )
        : variant === "download"
          ? joinClasses(
              "inline-flex select-none items-center rounded-pill border border-ink-950 bg-white font-sans font-bold text-ink-950 transition-[background-color,box-shadow,transform] duration-150 ease-out hover:!bg-accent-500 active:!bg-accent-500 disabled:cursor-not-allowed disabled:opacity-60",
              iconOnly
                ? "h-[35px] w-[70px] justify-center px-0 text-button-mobile shadow-[var(--shadow-button-mobile)]"
                : "h-[35px] w-[150px] justify-between gap-4 px-6 text-button-mobile shadow-[var(--shadow-button-mobile)] lg:h-10 lg:w-[199px] lg:text-button lg:shadow-[var(--shadow-button-desktop)]",
              isPressed && "!bg-accent-500",
              stateOverride === "hover" && "!bg-accent-500",
              stateOverride === "active" && "!bg-accent-500",
              !iconOnly && pressedClasses,
              iconOnly &&
                joinClasses(
                  "active:translate-x-[1px] active:translate-y-[4px] active:shadow-none",
                  stateOverride === "active" &&
                    "translate-x-[1px] translate-y-[4px] shadow-none",
                ),
              className,
            )
          : variant === "prjkt-ruby"
            ? joinClasses(
                "inline-flex select-none items-center rounded-pill border border-ink-950 bg-accent-500 px-6 py-[14px] text-ink-950 transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-white active:bg-white disabled:cursor-not-allowed disabled:opacity-60",
                "h-[57px] w-[346px] shadow-[var(--shadow-button-desktop)] lg:h-[50px] lg:w-[433px]",
                pressedClasses,
                className,
              )
            : joinClasses(
                "inline-flex items-center justify-center underline decoration-ink-950 underline-offset-4",
                "font-sans text-footnote-tight font-normal leading-4 text-ink-950",
                "lg:font-utility lg:text-body lg:leading-[1.4]",
                className,
              )

    const content =
      variant === "pill"
        ? renderPillContent(children, icon, centerLabel)
        : variant === "download"
          ? renderDownloadContent(children, iconOnly)
          : variant === "prjkt-ruby"
            ? renderPrjktContent(children, secondaryLabel, prjktWrapDesktop)
            : <span>{children}</span>

    if (href) {
      return (
        <a
          className={classes}
          data-leaving-site-interstitial={leavingSiteInterstitial}
          data-state-override={stateOverride}
          href={href}
          onClick={onClick}
          onMouseDown={handlePressStart}
          onMouseLeave={handlePressEnd}
          onMouseUp={handlePressEnd}
          onPointerCancel={handlePressEnd}
          onPointerDown={handlePressStart}
          onPointerLeave={handlePressEnd}
          onPointerUp={handlePressEnd}
          onTouchCancel={handlePressEnd}
          onTouchEnd={handlePressEnd}
          onTouchStart={handlePressStart}
          ref={ref as React.Ref<HTMLAnchorElement>}
          rel={resolvedRel}
          style={style}
          target={resolvedTarget}
        >
          {content}
        </a>
      )
    }

    return (
      <button
        className={classes}
        data-state-override={stateOverride}
        disabled={disabled}
        onClick={onClick}
        onMouseDown={handlePressStart}
        onMouseLeave={handlePressEnd}
        onMouseUp={handlePressEnd}
        onPointerCancel={handlePressEnd}
        onPointerDown={handlePressStart}
        onPointerLeave={handlePressEnd}
        onPointerUp={handlePressEnd}
        onTouchCancel={handlePressEnd}
        onTouchEnd={handlePressEnd}
        onTouchStart={handlePressStart}
        ref={ref as React.Ref<HTMLButtonElement>}
        style={style}
        type={type}
      >
        {content}
      </button>
    )
  },
)

Button.displayName = "Button"
